import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Paid/default tab config. A Free org reads+writes the FREE slot instead so a
// downgrade never destroys stored Paid selections — re-upgrading restores them.
const STORAGE_KEY = '@tabConfig:v1';
const FREE_STORAGE_KEY = '@tabConfig:v1:free';

export const TAB_SLOT_KEYS = ['(home)', '(search)', '(notifications)', '(settings)'];

export const DEFAULT_TAB_KEYS = {
    '(home)': 'home',
    '(search)': 'search',
    '(notifications)': 'notifications',
    '(settings)': 'settings',
};

const initialState = {
    customTabs: { ...DEFAULT_TAB_KEYS },
    isHydrated: false,
    planIsFree: false,
};

const useTabConfigStore = create((set, get) => {
    const storageKey = () => (get().planIsFree ? FREE_STORAGE_KEY : STORAGE_KEY);

    return {
        ...initialState,

        // Entitlements-driven plan context. Flipping it re-hydrates customTabs
        // from the matching storage slot.
        setPlanContext: (isFree) => {
            const next = isFree === true;
            if (get().planIsFree === next) return;
            set({ planIsFree: next });
            get().hydrate();
        },

        hydrate: async () => {
            try {
                const stored = await AsyncStorage.getItem(storageKey());
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (parsed && typeof parsed === 'object') {
                        const customTabs = {};
                        TAB_SLOT_KEYS.forEach((key) => {
                            customTabs[key] = parsed[key] || DEFAULT_TAB_KEYS[key];
                        });
                        set({ customTabs, isHydrated: true });
                        return;
                    }
                }
            } catch {
                // fall back to defaults
            }
            set({ customTabs: { ...DEFAULT_TAB_KEYS }, isHydrated: true });
        },

        setCustomTabs: async (customTabs) => {
            const next = {};
            TAB_SLOT_KEYS.forEach((key) => {
                next[key] = customTabs[key] || DEFAULT_TAB_KEYS[key];
            });
            try {
                await AsyncStorage.setItem(storageKey(), JSON.stringify(next));
            } catch {
                // silently fail
            }
            set({ customTabs: next });
        },

        resetToDefaults: async () => {
            try {
                await AsyncStorage.setItem(storageKey(), JSON.stringify(DEFAULT_TAB_KEYS));
            } catch {
                // silently fail
            }
            set({ customTabs: { ...DEFAULT_TAB_KEYS } });
        },

        getSelectedTabKeys: () => {
            return TAB_SLOT_KEYS.map((key) => get().customTabs[key] || DEFAULT_TAB_KEYS[key]);
        },

        isTabSelected: (viewKey) => {
            return get().getSelectedTabKeys().includes(viewKey);
        },
    };
});

export default useTabConfigStore;
